/**
 * @file Contributor UI — a1a3SummaryFromSubmission.
 * Derive A1–A3 summary numbers from submission + Background Data
 * (embodied energy / embodied carbon per finished material declared quantity).
 */

import {
  formatCompoundUnitSymbol,
  formatUnitSymbol,
} from "../../../../../utils/formatUnitSymbol";

/** Exported function — read Background Finished Declared Qty. */
export function readBackgroundFinishedDeclaredQty(submission: unknown): number | null {
  const s = submission as Record<string, unknown> | null | undefined;
  const bg = s?.background as Record<string, unknown> | undefined;
  const raw =
    bg?.finishedQuantity ??
    bg?.finished_quantity ??
    s?.finishedQuantity ??
    "";
  const q = parseFloat(String(raw ?? "").replace(/,/g, "").trim());
  return Number.isFinite(q) && q > 0 ? q : null;
}

/** Sum of embodied energy (MJ) across A1, A2, A3 energy rows. */
export function sumA1A2A3EmbodiedEnergyMj(submission: unknown): number {
  const s = submission as Record<string, unknown> | null | undefined;
  const e1 = sumEnergyRowsMj(s?.A1_Energy as unknown[] | undefined);
  const e2 = sumEnergyRowsMj(s?.A2_Energy as unknown[] | undefined);
  const e3 = sumEnergyRowsMj(s?.A3_Energy as unknown[] | undefined);
  return Math.round((e1 + e2 + e3) * 1000) / 1000;
}

/** @deprecated Use sumA1A2A3EmbodiedEnergyMj. */
export function sumA1A2A3EmbodiedEnergyKwh(submission: unknown): number {
  const mj = sumA1A2A3EmbodiedEnergyMj(submission);
  return mj > 0 ? Math.round((mj / 3.6) * 1000) / 1000 : 0;
}

/** Exported function — energy Row To Mj. */
export function energyRowToMj(row: Record<string, unknown>): number {
  const kwh = Number(row?.embodiedEnergyKwh ?? row?.embodied_energy_kwh);
  if (Number.isFinite(kwh) && kwh > 0) return kwh * 3.6;

  const ee = Number(row?.embodiedEnergy ?? row?.embodied_energy);
  if (!Number.isFinite(ee) || ee <= 0) return 0;

  const unitRaw = String(
    row?.embodiedEnergyUnit ??
      row?.embodied_energy_unit ??
      row?.unit ??
      row?.unitId ??
      row?.unit_id ??
      ""
  )
    .trim()
    .toLowerCase();
  if (unitRaw === "kwh" || unitRaw.includes("kilowatt")) return ee * 3.6;
  return ee;
}

function sumEnergyRowsMj(arr: unknown[] | undefined): number {
  if (!Array.isArray(arr)) return 0;
  return arr.reduce((acc, x) => {
    const row = x as Record<string, unknown>;
    return acc + energyRowToMj(row);
  }, 0);
}

/** Cumulative embodied energy (MJ) for one stage from saved energy rows or summary. */
export function sumStageEnergyMjFromSubmission(
  submission: unknown,
  energyKey: "A1_Energy" | "A2_Energy" | "A3_Energy",
  summaryKey: "a1Summary" | "a2Summary" | "a3Summary"
): number {
  const s = submission as Record<string, unknown> | null | undefined;
  if (!s) return 0;

  const summary = s[summaryKey] as Record<string, unknown> | undefined;
  const val = Number(summary?.embodiedEnergyValue ?? summary?.embodied_energy_value);
  if (Number.isFinite(val) && val > 0) {
    const unit = String(
      summary?.embodiedEnergyUnit ?? summary?.embodied_energy_unit ?? "MJ"
    )
      .trim()
      .toLowerCase();
    if (unit === "kwh" || unit.includes("kilowatt")) return val * 3.6;
    return val;
  }

  return sumEnergyRowsMj(s[energyKey] as unknown[] | undefined);
}

/** Exported function — sum A2Energy Mj For Summary. */
export function sumA2EnergyMjForSummary(submission: unknown): number {
  return sumStageEnergyMjFromSubmission(submission, "A2_Energy", "a2Summary");
}

/** Exported function — sum A3Energy Mj For Summary. */
export function sumA3EnergyMjForSummary(submission: unknown): number {
  return sumStageEnergyMjFromSubmission(submission, "A3_Energy", "a3Summary");
}

/** Cumulative embodied carbon (kg CO₂eq) for A3 from saved carbon rows. */
export function sumA3CarbonKgForSummary(submission: unknown): number {
  const s = submission as Record<string, unknown> | null | undefined;
  if (!s) return 0;
  const ref = sumCarbonReference(s.A3_Carbon as unknown[] | undefined);
  if (ref > 0) return Math.round(ref * 1000) / 1000;
  return Math.round(
    sumCarbonFunctional(s.A3_Carbon as unknown[] | undefined) * 1000
  ) / 1000;
}

function sumCarbonReference(arr: unknown[] | undefined): number {
  if (!Array.isArray(arr)) return 0;
  return arr.reduce((acc, x) => {
    const row = x as Record<string, unknown>;
    const n = Number(row?.referenceQty);
    return acc + (Number.isFinite(n) ? n : 0);
  }, 0);
}

function sumCarbonFunctional(arr: unknown[] | undefined): number {
  if (!Array.isArray(arr)) return 0;
  return arr.reduce((acc, x) => {
    const row = x as Record<string, unknown>;
    const n = Number(row?.functionalUnitCarbon);
    return acc + (Number.isFinite(n) ? n : 0);
  }, 0);
}

/**
 * Total embodied carbon reference across stages (prefer `referenceQty` per row;
 * if all zero, fall back to sum of `functionalUnitCarbon` for backward compatibility).
 */
export function sumA1A2A3EmbodiedCarbonTotal(submission: unknown): number {
  const s = submission as Record<string, unknown> | null | undefined;
  const r1 = sumCarbonReference(s?.A1_Carbon as unknown[] | undefined);
  const r2 = sumCarbonReference(s?.A2_Carbon as unknown[] | undefined);
  const r3 = sumCarbonReference(s?.A3_Carbon as unknown[] | undefined);
  const ref = r1 + r2 + r3;
  if (ref > 0) return Math.round(ref * 1e6) / 1e6;
  const f1 = sumCarbonFunctional(s?.A1_Carbon as unknown[] | undefined);
  const f2 = sumCarbonFunctional(s?.A2_Carbon as unknown[] | undefined);
  const f3 = sumCarbonFunctional(s?.A3_Carbon as unknown[] | undefined);
  return Math.round((f1 + f2 + f3) * 1e6) / 1e6;
}

/** Exported function — computed Embodied Energy Per Finished. */
export function computedEmbodiedEnergyPerFinished(
  submission: unknown
): number | null {
  const q = readBackgroundFinishedDeclaredQty(submission);
  if (q == null) return null;
  const total = sumA1A2A3EmbodiedEnergyMj(submission);
  if (total <= 0) return 0;
  return Math.round((total / q) * 1000) / 1000;
}

/** Exported function — computed Embodied Carbon Per Finished. */
export function computedEmbodiedCarbonPerFinished(
  submission: unknown
): number | null {
  const q = readBackgroundFinishedDeclaredQty(submission);
  if (q == null) return null;
  const total = sumA1A2A3EmbodiedCarbonTotal(submission);
  if (total <= 0) return 0;
  return Math.round((total / q) * 1000) / 1000;
}

/** Display string for kg CO₂e (Unicode subscript 2). */
export const A1A3_CARBON_NUMERATOR_UNIT = "kg CO₂e";

/** Exported function — read Background Sub Material Unit Raw. */
export function readBackgroundSubMaterialUnitRaw(
  submission: unknown
): unknown {
  const s = submission as Record<string, unknown> | null | undefined;
  const bg = s?.background as Record<string, unknown> | undefined;
  if (!bg) return null;
  const sym =
    bg.subMaterialUnitSymbol ?? bg.sub_material_unit_symbol ?? null;
  if (sym != null && String(sym).trim() !== "") return String(sym).trim();
  const su =
    bg.subMaterialUnit ?? bg.sub_material_unit ?? null;
  if (su != null && typeof su === "object") {
    const o = su as Record<string, unknown>;
    const t =
      o.symbol ?? o.unitSymbol ?? o.displayName ?? o.name ?? null;
    if (t != null && String(t).trim() !== "") return String(t).trim();
  }
  if (typeof su === "string") {
    const t = su.trim();
    if (t === "") return null;
    if (/^\d+$/.test(t)) return Number.parseInt(t, 10);
    return t;
  }
  if (typeof su === "number" && Number.isFinite(su)) return su;
  return null;
}

/**
 * Finished-material declared quantity unit (from Background), as a short symbol (e.g. kg, t, m²).
 * `unitIdToSymbol` maps CMS unit ids when `subMaterialUnit` is stored as a number.
 */
export function resolveFinishedQtyDenominatorSymbol(
  submission: unknown,
  unitIdToSymbol: Record<number, string>
): string | null {
  const raw = readBackgroundSubMaterialUnitRaw(submission);
  if (raw == null) return null;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) return null;
    if (/^\d+$/.test(t)) {
      const id = Number.parseInt(t, 10);
      return unitIdToSymbol[id]?.trim() || null;
    }
    return t;
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return unitIdToSymbol[raw]?.trim() || null;
  }
  return null;
}

/** Unit id → symbol from submission `referenceLabels.units` (readonly / overview). */
export function unitIdToSymbolFromReferenceLabels(
  submission: unknown
): Record<number, string> {
  const s = submission as Record<string, unknown> | null | undefined;
  const refs = s?.referenceLabels as Record<string, unknown> | undefined;
  const units = (refs?.units ?? {}) as Record<string, string>;
  const out: Record<number, string> = {};
  Object.entries(units).forEach(([id, label]) => {
    const n = Number(id);
    if (Number.isFinite(n)) out[n] = label;
  });
  return out;
}

/** Exported function — resolve Background Finished Material Unit Symbol. */
export function resolveBackgroundFinishedMaterialUnitSymbol(
  submission: unknown,
  unitIdToSymbol: Record<number, string> = unitIdToSymbolFromReferenceLabels(submission)
): string | null {
  return resolveFinishedQtyDenominatorSymbol(submission, unitIdToSymbol);
}

/** Stored/API unit for embodied energy per finished material qty (e.g. `MJ/kg`). */
export function perFinishedEnergyUnitValue(
  denominatorSymbol: string | null
): string {
  const d = denominatorSymbol?.trim();
  return d ? `MJ/${d}` : "MJ";
}

/** Stored/API unit for embodied carbon per finished material qty (e.g. `kg CO₂e/kg`). */
export function perFinishedCarbonUnitValue(
  denominatorSymbol: string | null
): string {
  const d = denominatorSymbol?.trim();
  return d ? `${A1A3_CARBON_NUMERATOR_UNIT}/${d}` : A1A3_CARBON_NUMERATOR_UNIT;
}

/** Display label for per-finished embodied energy unit. */
export function perFinishedEnergyUnitLabel(
  denominatorSymbol: string | null
): string {
  const v = perFinishedEnergyUnitValue(denominatorSymbol);
  return v.includes("/") ? formatCompoundUnitSymbol(v) : v;
}

/** Display label for per-finished embodied carbon unit. */
export function perFinishedCarbonUnitLabel(
  denominatorSymbol: string | null
): string {
  const v = perFinishedCarbonUnitValue(denominatorSymbol);
  return v.includes("/") ? formatCompoundUnitSymbol(v) : formatUnitSymbol(v);
}

/** Exported function — build A1A3Energy Unit Options. */
export function buildA1A3EnergyUnitOptions(
  denominatorSymbol: string | null
): { value: string; label: string }[] {
  const d = denominatorSymbol?.trim();
  if (d) {
    const value = `MJ/${d}`;
    return [
      { value, label: formatCompoundUnitSymbol(value) },
      { value: "MJ", label: "MJ" },
    ];
  }
  return [{ value: "MJ", label: "MJ" }];
}

/** Exported function — build A1A3Carbon Unit Options. */
export function buildA1A3CarbonUnitOptions(
  denominatorSymbol: string | null
): { value: string; label: string }[] {
  const d = denominatorSymbol?.trim();
  const n = A1A3_CARBON_NUMERATOR_UNIT;
  if (d) {
    const perValue = `${n}/${d}`;
    return [
      { value: perValue, label: formatCompoundUnitSymbol(perValue) },
      { value: n, label: formatUnitSymbol(n) },
    ];
  }
  return [{ value: n, label: formatUnitSymbol(n) }];
}

/** Exported function — pick Default A1A3Unit. */
export function pickDefaultA1A3Unit(
  saved: string,
  options: { value: string; label: string }[],
  preferPerDenominator: boolean
): string {
  const t = String(saved ?? "").trim();
  if (t && options.some((o) => o.value === t)) return t;
  if (preferPerDenominator) {
    const per = options.find((o) => o.value.includes("/"));
    if (per) return per.value;
  }
  return options[0]?.value ?? "";
}

/** Legacy UI stored long labels; treat as unset so we re-pick from Background denominator. */
export function normalizeLegacyA1A3UnitLabel(saved: string): string {
  const t = String(saved ?? "").trim();
  if (/per finished material qty/i.test(t)) return "";
  return t;
}

/** Exported function — sync A1A3Unit Selection. */
export function syncA1A3UnitSelection(
  saved: string,
  current: string,
  options: { value: string; label: string }[],
  preferPerDenominator: boolean
): string {
  const sv = normalizeLegacyA1A3UnitLabel(saved);
  if (sv && options.some((o) => o.value === sv)) return sv;
  const cv = String(current ?? "").trim();
  if (cv && options.some((o) => o.value === cv)) return cv;
  return pickDefaultA1A3Unit("", options, preferPerDenominator);
}
